import React, { useState, useEffect } from 'react'
import '../../addplant-style.scss'
import Grid from '@mui/material/Grid'
import useEquipmentDetailsStyles from './styles'
import { Box, Button, Typography } from '@mui/material'
import NextArrowLink from '../../../../assets/images/next_a.png'
import Delete from '../../../../assets/images/delete.png'
import Tab from '@mui/material/Tab'
import TabContext from '@mui/lab/TabContext'
import TabList from '@mui/lab/TabList'
import TabPanel from '@mui/lab/TabPanel'
import { get } from 'lodash'
import CustomTextField from '../../../../components/CustomTextField/CustomTextField'
import DropDown from '../../../../components/DropDown/DropDown'
import AutocompleteDropdown from '../../../../components/AutocompleteDropdown/AutocompleteDropdown'
import EstimatedGeneration from '../EstimatedGeneration/EstimatedGeneration'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormHelperText,
} from '@material-ui/core'
import ClearIcon from '@mui/icons-material/Clear'

// Dropdown options for device makes and models
const DATALOGGER_OPTIONS = {
  makes: ['Wattmon', 'de_datalogger_make'],
  models: ['Wattmon CSV Logger', 'de_datalogger_model'],
}

// Configuration for compatible devices based on datalogger
const DEVICE_COMPATIBILITY = {
  Wattmon: {
    'Wattmon CSV Logger': {
      energyMeter: {
        makes: ['Secure'],
        models: ['Elite300'],
      },
      inverter: {
        makes: ['Growatt'],
        models: ['MAX125KTL-X', 'MAX100KTL3-X'],
      },
      sensor: {
        makes: ['Suryalog'],
        models: ['SL-RS-485IRR-AT-PE'],
      },
    },
  },
  de_datalogger_make: {
    de_datalogger_model: {
      energyMeter: {
        makes: ['elite_mfm_make', 'elite_mfm_make_223', 'elite_mfm_make_201', 'Secure'],
        models: ['elite_mfm_model', 'elite_mfm_model_223', 'elite_mfm_model_201', 'elite_440_444'],
      },
      inverter: {
        makes: ['solis_inverter_make', 'growatt_inverter_make'],
        models: ['solis_inverter_model', 'growatt_inverter_model'],
      },
      sensor: {
        makes: ['Suryalogix', 'irradiation_sensor_make'],
        models: ['SL-RS-485IRR', 'irradiation_sensor_model'],
      },
    },
  },
}

// Fallback options (when no datalogger selected or for backward compatibility)
const DEFAULT_ENERGY_METER_OPTIONS = {
  makes: ['elite_mfm_make', 'elite_mfm_make_223', 'Multiple', 'elite_mfm_make_201', 'Secure'],
  models: ['elite_mfm_model', 'elite_mfm_model_223', 'Generic Energy Meter', 'elite_mfm_model_201', 'elite_440_444', 'Elite300'],
}

const DEFAULT_INVERTER_OPTIONS = {
  makes: ['solis_inverter_make', 'Multiple', 'Growatt', 'growatt_inverter_make'],
  models: ['solis_inverter_model', 'Generic Inverter', 'MAX100KTL3-X', 'MAX125KTL-X', 'growatt_inverter_model'],
}

const DEFAULT_SENSOR_OPTIONS = {
  makes: ['Suryalog', 'Suryalogix', 'irradiation_sensor_make'],
  models: ['SL-RS-485IRR-AT-PE', 'SL-RS-485IRR', 'irradiation_sensor_model'],
}

const EquipmentDetails = ({
  formik,
  devicelist,
  Edit,
  pyranometerSensorlist,
}) => {
  const classes = useEquipmentDetailsStyles()
  const [value, setValue] = React.useState(0)
  const [estimatedGTIOpen, setEstimatedGTIOpen] = React.useState(false)
  
  const handleChange = (event, newValue) => {
    setValue(newValue)
  }

  const handleestimatedGTIClose = () => {
    setEstimatedGTIOpen(false)
  }

  // Helper function to get compatible options based on datalogger
  const getCompatibleOptions = (roofIndex, deviceType) => {
    const dataloggerMake = formik.values.roofDetails?.[roofIndex]?.dataloggerMake
    const dataloggerModel = formik.values.roofDetails?.[roofIndex]?.dataloggerModel

    // If no datalogger selected, return default options
    if (!dataloggerMake || !dataloggerModel) {
      if (deviceType === 'energyMeter') return DEFAULT_ENERGY_METER_OPTIONS
      if (deviceType === 'inverter') return DEFAULT_INVERTER_OPTIONS
      if (deviceType === 'sensor') return DEFAULT_SENSOR_OPTIONS
    }

    // Get compatible options from configuration
    const compatible = DEVICE_COMPATIBILITY[dataloggerMake]?.[dataloggerModel]?.[deviceType]

    if (compatible) {
      return compatible
    }

    // Fallback to defaults if no match found
    if (deviceType === 'energyMeter') return DEFAULT_ENERGY_METER_OPTIONS
    if (deviceType === 'inverter') return DEFAULT_INVERTER_OPTIONS
    if (deviceType === 'sensor') return DEFAULT_SENSOR_OPTIONS
  }

  const [inputFields, setInputFields] = useState([
    {
      RoofId: '',
    },
  ])

  useEffect(() => {
    const uniqueRoofIds = new Set()
    formik.values.roofDetails.forEach((data) => {
      if (data && data.id) {
        uniqueRoofIds.add(data.id)
      }
    })

    setInputFields([...uniqueRoofIds].map((RoofId) => ({ RoofId })))
  }, [formik.values.roofDetails])

  const addInputField = () => {
    setInputFields([
      ...inputFields,
      {
        RoofId: '',
      },
    ])

    formik.values.roofDetails.push({
      mountingZoneLocation: '',
      mountingZoneType: '',
      orientation: '',
      f1ModuleFacing: '',
      f2ModuleFacing: '',
      t1TiltAngle: 0,
      t2TiltAngle: 0,
      a1Azimuth: 0,
      a2Azimuth: 0,
      m1Modules: 0,
      m2Modules: 0,
      noOfInverters: 0,
      noOfStrings: 0,
      dcCapacity: 0,
      serialNumber: '',
      energyMeterLocation: '',
      energyMeterMakeAndModel: '',
      ctRatio: 0,
      tiltAngle: 0,
      pyranometerLocation: '',
      pyranometerMakeAndModel: '',
      pyranometerSerialNumber: '',
      multiplyFactorGhiToGti: 0,
      deviceId: '',
      dataloggerMake: 'Wattmon',
      dataloggerModel: 'Wattmon CSV Logger',
      energyMeters: [
        {
          serialNumber: '',
          meterConnectedDcCapacity: 0,
          scalingFactor: 0,
          energyMeterMakeAndModel: '',
          ctRatio: 0,
          deviceMake: '',
          deviceModel: '',
          slaveId: '',
          channel: 1,
        },
      ],
      inverters: [
        {
          InverterSerialNumber: '',
          acCapacity: 0,
          dcCapacity: 0,
          noOfStrings: 0,
          deviceMake: '',
          deviceModel: '',
          slaveId: '',
          channel: 1,
        },
      ],
      pyranoMeter: {
        pyranoMeterSerialNumber: '',
        multiplyingFactor: 0,
        multiplyingFactorOg: 0,
        deviceMake: '',
        deviceModel: '',
        slaveId: 0,
        channel: 1,
      },
    })
  }

  const removeInputFields = (index) => {
    const rows = [...inputFields]
    rows.splice(index, 1)
    setInputFields(rows)
    formik.values.roofDetails.splice(index, 1)
  }

  const handleAddRoof = (index, evnt) => {
    const { name, value } = evnt.target
    const list = [...inputFields]
    list[index][name] = value
    setInputFields(list)
  }

  const addInverter = (roofIndex) => {
    const newInverters = [...formik.values.roofDetails[roofIndex].inverters]
    newInverters.push({
      InverterSerialNumber: '',
      acCapacity: 0,
      dcCapacity: 0,
      noOfStrings: 0,
      deviceMake: '',
      deviceModel: '',
      slaveId: '',
      channel: 1,
      deviceTypeId: '',
    })
    formik.setFieldValue(`roofDetails.${roofIndex}.inverters`, newInverters)
  }

  const removeInverter = (roofIndex, invIndex) => {
    const newInverters = [...formik.values.roofDetails[roofIndex].inverters]
    newInverters.splice(invIndex, 1)
    formik.setFieldValue(`roofDetails.${roofIndex}.inverters`, newInverters)
  }

  return (
    <div className={classes.root}>
      <div className="ptb_20">
        <Box>
          <div style={{ display: 'flex', flexDirection: 'row' }}>
            <Grid
              container
              spacing={2}
              className="mb_20"
              style={{ width: '90%' }}
            >
              {formik?.values?.roofDetails?.map((data, index) => (
                <Grid item xs={12} md={3} key={index}>
                  <div className="">
                    <div className="align_start">
                      <div className="equipment_details">
                        <div className="align_btw" style={{ height: '56px' }}>
                          <div>Roof {index + 1}</div>
                          <img
                            src={NextArrowLink}
                            alt="Next"
                            className="pointer"
                          />
                        </div>
                      </div>
                      <div className="delete_i">
                        <img
                          onClick={() => removeInputFields(index)}
                          src={Delete}
                          alt="delete"
                          className="delete_i_w pointer"
                        />
                      </div>
                    </div>
                  </div>
                </Grid>
              ))}
            </Grid>
            <Grid
              style={{
                width: '10%',
                display: 'flex',
                justifyContent: 'flex-end',
              }}
              className="mb_20"
            >
              <Button
                style={{
                  backgroundColor: 'rgba(145, 215, 250, 0.29)',
                  borderRadius: '80%',
                  height: '60px',
                  width: '50px',
                }}
                onClick={addInputField}
              >
                +
              </Button>
            </Grid>
          </div>
        </Box>

        <br />
        <br />

        <Box>
          {formik?.values?.roofDetails?.map((data, index) => (
            <TabContext value={value} key={index}>
              <Box className="page_h_b2">
                <TabList
                  onChange={handleChange}
                  aria-label="lab API tabs example"
                  centered
                >
                  <Tab
                    classes={{ root: 'MuiButtonBase-root-MuiTab-root' }}
                    label="General"
                    value={`1${index + 1}`}
                  />
                  <Tab
                    classes={{ root: 'MuiButtonBase-root-MuiTab-root' }}
                    label="Inverter"
                    value={`2${index + 1}`}
                  />
                  <Tab
                    classes={{ root: 'MuiButtonBase-root-MuiTab-root' }}
                    label="Energy Meter"
                    value={`3${index + 1}`}
                  />
                  <Tab
                    classes={{ root: 'MuiButtonBase-root-MuiTab-root' }}
                    label="Pyranometer"
                    value={`4${index + 1}`}
                  />
                </TabList>
                <div className="tab_header">Roof {index + 1}</div>
              </Box>

              <TabPanel value={`1${index + 1}`}>
                <Box
                  sx={{
                    '& .MuiTextField-root': { m: 1, width: '100%' },
                  }}
                  noValidate
                  autoComplete="off"
                >
                  <Grid container spacing={2} className="mb_20">
                    <Grid item xs={12} md={5}>
                      <div className=" textfield mb_20">
                        <CustomTextField
                          label="Mounting Zone Location"
                          id="filled-size-small"
                          value={
                            formik.values?.roofDetails?.[index]
                              ?.mountingZoneLocation
                          }
                          onChange={(value) =>
                            formik.setFieldValue(
                              `roofDetails.${index}.mountingZoneLocation`,
                              value.target.value
                            )
                          }
                          error={
                            formik.errors?.roofDetails?.[index]
                              ?.mountingZoneLocation
                          }
                          helperText={
                            get(
                              formik,
                              `touched.roofDetails.${index}.mountingZoneLocation`
                            ) &&
                            get(
                              formik,
                              `errors.roofDetails.${index}.mountingZoneLocation`
                            )
                          }
                        />
                      </div>
                      <div className=" textfield  mb_20">
                        <CustomTextField
                          label="Mounting Zone Type"
                          id="filled-size-small"
                          value={
                            formik.values.roofDetails?.[index]?.mountingZoneType
                          }
                          onChange={(value) =>
                            formik.setFieldValue(
                              `roofDetails.${index}.mountingZoneType`,
                              value.target.value
                            )
                          }
                          error={
                            formik.errors?.roofDetails?.[index]
                              ?.mountingZoneType
                          }
                          helperText={
                            get(
                              formik,
                              `touched.roofDetails.${index}.mountingZoneType`
                            ) &&
                            get(
                              formik,
                              `errors.roofDetails.${index}.mountingZoneType`
                            )
                          }
                        />
                      </div>
                      <div className=" textfield  mb_20">
                        <CustomTextField
                          label="Mac ID"
                          id="filled-size-small"
                          value={
                            formik.values.roofDetails?.[index]?.macId ||
                            formik.values.roofDetails?.[index]?.deviceId || ''
                          }
                          onChange={(e) => {
                            const val = e.target.value
                            formik.setFieldValue(
                              `roofDetails.${index}.macId`,
                              val
                            )
                            formik.setFieldValue(
                              `roofDetails.${index}.deviceId`,
                              val
                            )
                          }}
                          error={
                            formik.errors?.roofDetails?.[index]?.deviceId ||
                            formik.errors?.roofDetails?.[index]?.macId
                          }
                          helperText={
                            (get(
                              formik,
                              `touched.roofDetails.${index}.macId`
                            ) &&
                              get(formik, `errors.roofDetails.${index}.macId`)) ||
                            (get(
                              formik,
                              `touched.roofDetails.${index}.deviceId`
                            ) &&
                              get(formik, `errors.roofDetails.${index}.deviceId`))
                          }
                        />
                      </div>
                      <div className=" textfield  mb_20">
                        <AutocompleteDropdown
                          label="Datalogger Make"
                          value={
                            formik.values.roofDetails?.[index]?.dataloggerMake
                          }
                          onChange={(value) =>
                            formik.setFieldValue(
                              `roofDetails.${index}.dataloggerMake`,
                              value.target.value
                            )
                          }
                          options={DATALOGGER_OPTIONS.makes}
                          error={
                            formik.errors?.roofDetails?.[index]?.dataloggerMake
                          }
                          helperText={
                            get(
                              formik,
                              `touched.roofDetails.${index}.dataloggerMake`
                            ) &&
                            get(
                              formik,
                              `errors.roofDetails.${index}.dataloggerMake`
                            )
                          }
                          placeholder="Select or type custom value"
                        />
                      </div>
                      <div className=" textfield  mb_20">
                        <AutocompleteDropdown
                          label="Datalogger Model"
                          value={
                            formik.values.roofDetails?.[index]?.dataloggerModel
                          }
                          onChange={(value) =>
                            formik.setFieldValue(
                              `roofDetails.${index}.dataloggerModel`,
                              value.target.value
                            )
                          }
                          options={DATALOGGER_OPTIONS.models}
                          error={
                            formik.errors?.roofDetails?.[index]?.dataloggerModel
                          }
                          helperText={
                            get(
                              formik,
                              `touched.roofDetails.${index}.dataloggerModel`
                            ) &&
                            get(
                              formik,
                              `errors.roofDetails.${index}.dataloggerModel`
                            )
                          }
                          placeholder="Select or type custom value"
                        />
                      </div>
                    </Grid>
                    <Grid item xs={12} md={2}></Grid>
                    <Grid item xs={12} md={5}>
                      <div className=" textfield  mb_20">
                        <DropDown
                          labelId="demo-simple-select-filled-label"
                          label="Orientation"
                          id="demo-simple-select-filled"
                          value={
                            formik.values.roofDetails?.[index]?.orientation
                          }
                          onChange={(value) =>
                            formik.setFieldValue(
                              `roofDetails.${index}.orientation`,
                              value.target.value
                            )
                          }
                          error={
                            formik.errors?.roofDetails?.[index]?.orientation
                          }
                          helperText={
                            get(
                              formik,
                              `touched.roofDetails.${index}.orientation`
                            ) &&
                            get(
                              formik,
                              `errors.roofDetails.${index}.orientation`
                            )
                          }
                          MenuData={[
                            'East',
                            'West',
                            'North',
                            'South',
                            'North East',
                            'South East',
                            'South West',
                            'North West',
                          ]}
                          rectangle={true}
                        ></DropDown>
                      </div>
                      <Box
                        sx={{
                          '& .MuiTextField-root': { m: 1, width: '80%' },
                        }}
                        noValidate
                        autoComplete="off"
                      >
                        <Grid container spacing={2} className="textfield">
                          <Grid item xs={12} md={6}>
                            <div className="align_start">
                              <div className="orient_sub_h">F1</div>
                              <CustomTextField
                                label="Module facing"
                                id="filled-size-small"
                                value={
                                  formik.values.roofDetails?.[index]
                                    ?.f1ModuleFacing
                                }
                                onChange={(value) =>
                                  formik.setFieldValue(
                                    `roofDetails.${index}.f1ModuleFacing`,
                                    value.target.value
                                  )
                                }
                                error={
                                  formik.errors?.roofDetails?.[index]
                                    ?.f1ModuleFacing
                                }
                                helperText={
                                  get(
                                    formik,
                                    `touched.roofDetails.${index}.f1ModuleFacing`
                                  ) &&
                                  get(
                                    formik,
                                    `errors.roofDetails.${index}.f1ModuleFacing`
                                  )
                                }
                              />
                            </div>
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <div className="align_start">
                              <div className="orient_sub_h">F2</div>
                              <CustomTextField
                                label="Module facing"
                                id="filled-size-small"
                                value={
                                  formik.values.roofDetails?.[index]
                                    ?.f2ModuleFacing
                                }
                                onChange={(value) =>
                                  formik.setFieldValue(
                                    `roofDetails.${index}.f2ModuleFacing`,
                                    value.target.value
                                  )
                                }
                                error={
                                  formik.errors?.roofDetails?.[index]
                                    ?.f2ModuleFacing
                                }
                                helperText={
                                  get(
                                    formik,
                                    `touched.roofDetails.${index}.f2ModuleFacing`
                                  ) &&
                                  get(
                                    formik,
                                    `errors.roofDetails.${index}.f2ModuleFacing`
                                  )
                                }
                              />
                            </div>
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <div className="align_start">
                              <div className="orient_sub_h">T1</div>
                              <CustomTextField
                                label="Tilt angle"
                                id="filled-size-small"
                                value={
                                  formik.values.roofDetails?.[index]
                                    ?.t1TiltAngle
                                }
                                onChange={(value) =>
                                  formik.setFieldValue(
                                    `roofDetails.${index}.t1TiltAngle`,
                                    Number(value.target.value)
                                  )
                                }
                                type="number"
                                error={
                                  formik.errors?.roofDetails?.[index]
                                    ?.t1TiltAngle
                                }
                                helperText={
                                  get(
                                    formik,
                                    `touched.roofDetails.${index}.t1TiltAngle`
                                  ) &&
                                  get(
                                    formik,
                                    `errors.roofDetails.${index}.t1TiltAngle`
                                  )
                                }
                              />
                            </div>
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <div className="align_start">
                              <div className="orient_sub_h">T2</div>
                              <CustomTextField
                                label="Tilt angle"
                                id="filled-size-small"
                                value={
                                  formik.values.roofDetails?.[index]
                                    ?.t2TiltAngle
                                }
                                onChange={(value) =>
                                  formik.setFieldValue(
                                    `roofDetails.${index}.t2TiltAngle`,
                                    Number(value.target.value)
                                  )
                                }
                                type="number"
                                error={
                                  formik.errors?.roofDetails?.[index]
                                    ?.t2TiltAngle
                                }
                                helperText={
                                  get(
                                    formik,
                                    `touched.roofDetails.${index}.t2TiltAngle`
                                  ) &&
                                  get(
                                    formik,
                                    `errors.roofDetails.${index}.t2TiltAngle`
                                  )
                                }
                              />
                            </div>
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <div className="align_start">
                              <div className="orient_sub_h">A1</div>
                              <CustomTextField
                                label="Azimuth"
                                id="filled-size-small"
                                value={
                                  formik.values.roofDetails?.[index]?.a1Azimuth
                                }
                                onChange={(value) =>
                                  formik.setFieldValue(
                                    `roofDetails.${index}.a1Azimuth`,
                                    Number(value.target.value)
                                  )
                                }
                                type="number"
                                error={
                                  formik.errors?.roofDetails?.[index]?.a1Azimuth
                                }
                                helperText={
                                  get(
                                    formik,
                                    `touched.roofDetails.${index}.a1Azimuth`
                                  ) &&
                                  get(
                                    formik,
                                    `errors.roofDetails.${index}.a1Azimuth`
                                  )
                                }
                              />
                            </div>
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <div className="align_start">
                              <div className="orient_sub_h">A2</div>
                              <CustomTextField
                                label="Azimuth"
                                id="filled-size-small"
                                value={
                                  formik.values.roofDetails?.[index]?.a2Azimuth
                                }
                                onChange={(value) =>
                                  formik.setFieldValue(
                                    `roofDetails.${index}.a2Azimuth`,
                                    Number(value.target.value)
                                  )
                                }
                                type="number"
                                error={
                                  formik.errors?.roofDetails?.[index]?.a2Azimuth
                                }
                                helperText={
                                  get(
                                    formik,
                                    `touched.roofDetails.${index}.a2Azimuth`
                                  ) &&
                                  get(
                                    formik,
                                    `errors.roofDetails.${index}.a2Azimuth`
                                  )
                                }
                              />
                            </div>
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <div className="align_start">
                              <div className="orient_sub_h">M1</div>
                              <CustomTextField
                                label="Modules"
                                id="filled-size-small"
                                value={
                                  formik.values.roofDetails?.[index]?.m1Modules
                                }
                                onChange={(value) =>
                                  formik.setFieldValue(
                                    `roofDetails.${index}.m1Modules`,
                                    Number(value.target.value)
                                  )
                                }
                                type="number"
                                error={
                                  formik.errors?.roofDetails?.[index]?.m1Modules
                                }
                                helperText={
                                  get(
                                    formik,
                                    `touched.roofDetails.${index}.m1Modules`
                                  ) &&
                                  get(
                                    formik,
                                    `errors.roofDetails.${index}.m1Modules`
                                  )
                                }
                              />
                            </div>
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <div className="align_start">
                              <div className="orient_sub_h">M2</div>
                              <CustomTextField
                                label="Modules"
                                id="filled-size-small"
                                value={
                                  formik.values.roofDetails?.[index]?.m2Modules
                                }
                                onChange={(value) =>
                                  formik.setFieldValue(
                                    `roofDetails.${index}.m2Modules`,
                                    Number(value.target.value)
                                  )
                                }
                                type="number"
                                error={
                                  formik.errors?.roofDetails?.[index]?.m2Modules
                                }
                                helperText={
                                  get(
                                    formik,
                                    `touched.roofDetails.${index}.m2Modules`
                                  ) &&
                                  get(
                                    formik,
                                    `errors.roofDetails.${index}.m2Modules`
                                  )
                                }
                              />
                            </div>
                          </Grid>
                        </Grid>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              </TabPanel>
              {/* INVERTER TAB */}
              <TabPanel value={`2${index + 1}`}>
                <Box
                  sx={{
                    '& .MuiTextField-root': { m: 1, width: '100%' },
                  }}
                  noValidate
                  autoComplete="off"
                >
                  {formik.values.roofDetails[index].inverters.map((inv, invIndex) => {
                    const inverterOptions = getCompatibleOptions(index, 'inverter')
                    return (
                      <div key={invIndex} style={{ marginBottom: '20px', position: 'relative' }}>
                        <Typography variant="h6">Inverter {invIndex + 1}</Typography>
                        {formik.values.roofDetails[index].inverters.length > 1 && (
                          <img
                            src={Delete}
                            alt="delete"
                            style={{ position: 'absolute', top: 0, right: 0, cursor: 'pointer' }}
                            onClick={() => removeInverter(index, invIndex)}
                          />
                        )}
                        <Grid container spacing={2} className="mb_20">
                          <Grid item xs={12} md={4}>
                            <div className=" textfield ">
                              <CustomTextField
                                label="Inverter Serial Number"
                                id="filled-size-small"
                                value={
                                  formik.values.roofDetails[index]?.inverters?.[invIndex]
                                    ?.InverterSerialNumber
                                }
                                onChange={(value) =>
                                  formik.setFieldValue(
                                    `roofDetails.${index}.inverters.${invIndex}.InverterSerialNumber`,
                                    value.target.value
                                  )
                                }
                                error={
                                  formik.errors?.roofDetails?.[index]?.inverters?.[invIndex]
                                    ?.InverterSerialNumber
                                }
                                helperText={
                                  get(
                                    formik,
                                    `touched.roofDetails.${index}.inverters.${invIndex}.InverterSerialNumber`
                                  ) &&
                                  get(
                                    formik,
                                    `errors.roofDetails.${index}.inverters.${invIndex}.InverterSerialNumber`
                                  )
                                }
                              />
                            </div>
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <div className=" textfield ">
                              <CustomTextField
                                label="AC Capacity"
                                id="filled-size-small"
                                type="number"
                                value={
                                  formik.values.roofDetails[index]?.inverters?.[invIndex]
                                    ?.acCapacity
                                }
                                onChange={(value) =>
                                  formik.setFieldValue(
                                    `roofDetails.${index}.inverters.${invIndex}.acCapacity`,
                                    Number(value.target.value)
                                  )
                                }
                                error={
                                  formik.errors?.roofDetails?.[index]?.inverters?.[invIndex]
                                    ?.acCapacity
                                }
                                helperText={
                                  get(
                                    formik,
                                    `touched.roofDetails.${index}.inverters.${invIndex}.acCapacity`
                                  ) &&
                                  get(
                                    formik,
                                    `errors.roofDetails.${index}.inverters.${invIndex}.acCapacity`
                                  )
                                }
                              />
                            </div>
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <div className=" textfield ">
                              <CustomTextField
                                label="DC Capacity"
                                id="filled-size-small"
                                type="number"
                                value={
                                  formik.values.roofDetails[index]?.inverters?.[invIndex]
                                    ?.dcCapacity
                                }
                                onChange={(value) =>
                                  formik.setFieldValue(
                                    `roofDetails.${index}.inverters.${invIndex}.dcCapacity`,
                                    Number(value.target.value)
                                  )
                                }
                                error={
                                  formik.errors?.roofDetails?.[index]?.inverters?.[invIndex]
                                    ?.dcCapacity
                                }
                                helperText={
                                  get(
                                    formik,
                                    `touched.roofDetails.${index}.inverters.${invIndex}.dcCapacity`
                                  ) &&
                                  get(
                                    formik,
                                    `errors.roofDetails.${index}.inverters.${invIndex}.dcCapacity`
                                  )
                                }
                              />
                            </div>
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <div className=" textfield ">
                              <CustomTextField
                                label="Number of Strings"
                                id="filled-size-small"
                                type="number"
                                value={
                                  formik.values.roofDetails[index]?.inverters?.[invIndex]
                                    ?.noOfStrings
                                }
                                onChange={(value) =>
                                  formik.setFieldValue(
                                    `roofDetails.${index}.inverters.${invIndex}.noOfStrings`,
                                    Number(value.target.value)
                                  )
                                }
                                error={
                                  formik.errors?.roofDetails?.[index]?.inverters?.[invIndex]
                                    ?.noOfStrings
                                }
                                helperText={
                                  get(
                                    formik,
                                    `touched.roofDetails.${index}.inverters.${invIndex}.noOfStrings`
                                  ) &&
                                  get(
                                    formik,
                                    `errors.roofDetails.${index}.inverters.${invIndex}.noOfStrings`
                                  )
                                }
                              />
                            </div>
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <div className=" textfield ">
                              <AutocompleteDropdown
                                label="Device Make"
                                value={
                                  formik.values.roofDetails[index]?.inverters?.[invIndex]
                                    ?.deviceMake
                                }
                                onChange={(value) =>
                                  formik.setFieldValue(
                                    `roofDetails.${index}.inverters.${invIndex}.deviceMake`,
                                    value.target.value
                                  )
                                }
                                options={inverterOptions.makes}
                                error={
                                  formik.errors?.roofDetails?.[index]?.inverters?.[invIndex]
                                    ?.deviceMake
                                }
                                helperText={
                                  get(
                                    formik,
                                    `touched.roofDetails.${index}.inverters.${invIndex}.deviceMake`
                                  ) &&
                                  get(
                                    formik,
                                    `errors.roofDetails.${index}.inverters.${invIndex}.deviceMake`
                                  )
                                }
                                placeholder="Select or type custom value"
                              />
                            </div>
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <div className=" textfield ">
                              <AutocompleteDropdown
                                label="Device Model"
                                value={
                                  formik.values.roofDetails[index]?.inverters?.[invIndex]
                                    ?.deviceModel
                                }
                                onChange={(value) =>
                                  formik.setFieldValue(
                                    `roofDetails.${index}.inverters.${invIndex}.deviceModel`,
                                    value.target.value
                                  )
                                }
                                options={inverterOptions.models}
                                error={
                                  formik.errors?.roofDetails?.[index]?.inverters?.[invIndex]
                                    ?.deviceModel
                                }
                                helperText={
                                  get(
                                    formik,
                                    `touched.roofDetails.${index}.inverters.${invIndex}.deviceModel`
                                  ) &&
                                  get(
                                    formik,
                                    `errors.roofDetails.${index}.inverters.${invIndex}.deviceModel`
                                  )
                                }
                                placeholder="Select or type custom value"
                              />
                            </div>
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <div className=" textfield ">
                              <CustomTextField
                                label="Slave ID"
                                id="filled-size-small"
                                disabled={Edit}
                                type="number"
                                value={
                                  formik.values.roofDetails[index]?.inverters?.[invIndex]
                                    ?.slaveId
                                }
                                onChange={(value) =>
                                  formik.setFieldValue(
                                    `roofDetails.${index}.inverters.${invIndex}.slaveId`,
                                    Number(value.target.value)
                                  )
                                }
                                error={
                                  formik.errors?.roofDetails?.[index]?.inverters?.[invIndex]
                                    ?.slaveId
                                }
                                helperText={
                                  get(
                                    formik,
                                    `touched.roofDetails.${index}.inverters.${invIndex}.slaveId`
                                  ) &&
                                  get(
                                    formik,
                                    `errors.roofDetails.${index}.inverters.${invIndex}.slaveId`
                                  )
                                }
                              />
                            </div>
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <div className=" textfield ">
                              <CustomTextField
                                label="Channel"
                                id="filled-size-small"
                                type="number"
                                value={
                                  formik.values.roofDetails[index]?.inverters?.[invIndex]
                                    ?.channel || 1
                                }
                                onChange={(value) =>
                                  formik.setFieldValue(
                                    `roofDetails.${index}.inverters.${invIndex}.channel`,
                                    Number(value.target.value)
                                  )
                                }
                              />
                            </div>
                          </Grid>
                        </Grid>
                      </div>
                    )
                  })}
                  <Button
                    style={{
                      backgroundColor: 'rgba(145, 215, 250, 0.29)',
                      borderRadius: '4px',
                      height: '40px',
                    }}
                    onClick={() => addInverter(index)}
                  >
                    + Add Inverter
                  </Button>
                </Box>
              </TabPanel>
              {/* ENERGY METER TAB - FIXED VERSION */}
              <TabPanel value={`3${index + 1}`}>
                <Box
                  sx={{
                    '& .MuiTextField-root': { m: 1, width: '100%' },
                  }}
                  noValidate
                  autoComplete="off"
                >
                  {(() => {
                    const energyMeterOptions = getCompatibleOptions(index, 'energyMeter')
                    return (
                      <Grid container spacing={3} className="mb_20">
                        <Grid item xs={12} md={4}>
                          <div className="textfield">
                            <CustomTextField
                              label="Serial No."
                              id="filled-size-small"
                              value={
                                formik.values.roofDetails?.[index]
                                  ?.energyMeters?.[0]?.serialNumber
                              }
                              onChange={(value) =>
                                formik.setFieldValue(
                                  `roofDetails.${index}.energyMeters.${0}.serialNumber`,
                                  value.target.value
                                )
                              }
                              error={
                                formik.errors?.roofDetails?.[index]
                                  ?.energyMeters?.[0]?.serialNumber
                              }
                              helperText={
                                get(
                                  formik,
                                  `touched.roofDetails.${index}.energyMeters.${0}.serialNumber`
                                ) &&
                                get(
                                  formik,
                                  `errors.roofDetails.${index}.energyMeters.${0}.serialNumber`
                                )
                              }
                            />
                          </div>
                        </Grid>

                        <Grid item xs={12} md={4}>
                          <div className="textfield">
                            <CustomTextField
                              label="CT Ratio"
                              id="filled-size-small"
                              type="number"
                              value={
                                formik.values.roofDetails?.[index]
                                  ?.energyMeters?.[0]?.ctRatio
                              }
                              onChange={(value) =>
                                formik.setFieldValue(
                                  `roofDetails.${index}.energyMeters.${0}.ctRatio`,
                                  Number(value.target.value)
                                )
                              }
                              error={
                                formik.errors?.roofDetails?.[index]
                                  ?.energyMeters?.[0]?.ctRatio
                              }
                              helperText={
                                get(
                                  formik,
                                  `touched.roofDetails.${index}.energyMeters.${0}.ctRatio`
                                ) &&
                                get(
                                  formik,
                                  `errors.roofDetails.${index}.energyMeters.${0}.ctRatio`
                                )
                              }
                            />
                          </div>
                        </Grid>

                        <Grid item xs={12} md={4}>
                          <div className="textfield">
                            <CustomTextField
                              label="Make & Model"
                              id="filled-size-small"
                              value={
                                formik.values.roofDetails?.[index]
                                  ?.energyMeters?.[0]?.energyMeterMakeAndModel
                              }
                              onChange={(value) =>
                                formik.setFieldValue(
                                  `roofDetails.${index}.energyMeters.${0}.energyMeterMakeAndModel`,
                                  value.target.value
                                )
                              }
                              error={
                                formik.errors?.roofDetails?.[index]
                                  ?.energyMeters?.[0]?.energyMeterMakeAndModel
                              }
                              helperText={
                                get(
                                  formik,
                                  `touched.roofDetails.${index}.energyMeters.${0}.energyMeterMakeAndModel`
                                ) &&
                                get(
                                  formik,
                                  `errors.roofDetails.${index}.energyMeters.${0}.energyMeterMakeAndModel`
                                )
                              }
                            />
                          </div>
                        </Grid>

                        <Grid item xs={12} md={4}>
                          <div className="textfield">
                            <CustomTextField
                              label="Meter Connected DC Capacity"
                              id="filled-size-small"
                              type="number"
                              value={
                                formik.values.roofDetails?.[index]
                                  ?.energyMeters?.[0]?.meterConnectedDcCapacity
                              }
                              onChange={(value) =>
                                formik.setFieldValue(
                                  `roofDetails.${index}.energyMeters.${0}.meterConnectedDcCapacity`,
                                  Number(value.target.value)
                                )
                              }
                              error={
                                formik.errors?.roofDetails?.[index]
                                  ?.energyMeters?.[0]?.meterConnectedDcCapacity
                              }
                              helperText={
                                get(
                                  formik,
                                  `touched.roofDetails.${index}.energyMeters.${0}.meterConnectedDcCapacity`
                                ) &&
                                get(
                                  formik,
                                  `errors.roofDetails.${index}.energyMeters.${0}.meterConnectedDcCapacity`
                                )
                              }
                            />
                          </div>
                        </Grid>

                        <Grid item xs={12} md={4}>
                          <div className="textfield">
                            <CustomTextField
                              label="Scaling Factor"
                              id="filled-size-small"
                              type="number"
                              value={
                                formik.values.roofDetails?.[index]
                                  ?.energyMeters?.[0]?.scalingFactor
                              }
                              onChange={(value) =>
                                formik.setFieldValue(
                                  `roofDetails.${index}.energyMeters.${0}.scalingFactor`,
                                  Number(value.target.value)
                                )
                              }
                              error={
                                formik.errors?.roofDetails?.[index]
                                  ?.energyMeters?.[0]?.scalingFactor
                              }
                              helperText={
                                get(
                                  formik,
                                  `touched.roofDetails.${index}.energyMeters.${0}.scalingFactor`
                                ) &&
                                get(
                                  formik,
                                  `errors.roofDetails.${index}.energyMeters.${0}.scalingFactor`
                                )
                              }
                            />
                          </div>
                        </Grid>

                        <Grid item xs={12} md={4}>
                          <div className="textfield">
                            <AutocompleteDropdown
                              label="Device Make"
                              value={
                                formik.values.roofDetails?.[index]
                                  ?.energyMeters?.[0]?.deviceMake
                              }
                              onChange={(value) =>
                                formik.setFieldValue(
                                  `roofDetails.${index}.energyMeters.${0}.deviceMake`,
                                  value.target.value
                                )
                              }
                              options={energyMeterOptions.makes}
                              error={
                                formik.errors?.roofDetails?.[index]
                                  ?.energyMeters?.[0]?.deviceMake
                              }
                              helperText={
                                get(
                                  formik,
                                  `touched.roofDetails.${index}.energyMeters.${0}.deviceMake`
                                ) &&
                                get(
                                  formik,
                                  `errors.roofDetails.${index}.energyMeters.${0}.deviceMake`
                                )
                              }
                              placeholder="Select or type custom value"
                            />
                          </div>
                        </Grid>

                        <Grid item xs={12} md={4}>
                          <div className="textfield">
                            <AutocompleteDropdown
                              label="Device Model"
                              value={
                                formik.values.roofDetails?.[index]
                                  ?.energyMeters?.[0]?.deviceModel
                              }
                              onChange={(value) =>
                                formik.setFieldValue(
                                  `roofDetails.${index}.energyMeters.${0}.deviceModel`,
                                  value.target.value
                                )
                              }
                              options={energyMeterOptions.models}
                              error={
                                formik.errors?.roofDetails?.[index]
                                  ?.energyMeters?.[0]?.deviceModel
                              }
                              helperText={
                                get(
                                  formik,
                                  `touched.roofDetails.${index}.energyMeters.${0}.deviceModel`
                                ) &&
                                get(
                                  formik,
                                  `errors.roofDetails.${index}.energyMeters.${0}.deviceModel`
                                )
                              }
                              placeholder="Select or type custom value"
                            />
                          </div>
                        </Grid>

                        <Grid item xs={12} md={4}>
                          <div className="textfield">
                            <CustomTextField
                              label="Slave ID"
                              id="filled-size-small"
                              disabled={Edit}
                              type="number"
                              value={
                                formik.values.roofDetails?.[index]
                                  ?.energyMeters?.[0]?.slaveId
                              }
                              onChange={(value) =>
                                formik.setFieldValue(
                                  `roofDetails.${index}.energyMeters.${0}.slaveId`,
                                  Number(value.target.value)
                                )
                              }
                              error={
                                formik.errors?.roofDetails?.[index]
                                  ?.energyMeters?.[0]?.slaveId
                              }
                              helperText={
                                get(
                                  formik,
                                  `touched.roofDetails.${index}.energyMeters.${0}.slaveId`
                                ) &&
                                get(
                                  formik,
                                  `errors.roofDetails.${index}.energyMeters.${0}.slaveId`
                                )
                              }
                            />
                          </div>
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <div className="textfield">
                            <CustomTextField
                              label="Channel"
                              id="filled-size-small"
                              type="number"
                              value={
                                formik.values.roofDetails?.[index]
                                  ?.energyMeters?.[0]?.channel || 1
                              }
                              onChange={(value) =>
                                formik.setFieldValue(
                                  `roofDetails.${index}.energyMeters.${0}.channel`,
                                  Number(value.target.value)
                                )
                              }
                            />
                          </div>
                        </Grid>
                      </Grid>
                    )
                  })()}
                </Box>
              </TabPanel>
              <TabPanel value={`4${index + 1}`}>
                <Box
                  sx={{
                    '& .MuiTextField-root': { mt: 1, width: '100%' },
                  }}
                  noValidate
                  autoComplete="off"
                >
                  {(() => {
                    const sensorOptions = getCompatibleOptions(index, 'sensor')
                    return (
                      <Grid container spacing={2} className="mb_20">
                        <Grid item xs={12} md={12} style={{ marginBottom: '20px' }}>
                          <div className="y_n_toggle">
                            <div className="contractor_access">
                              <div className="align_btw" style={{ height: '56px' }}>
                                <div className="toggle_txt">PyranoMeter</div>
                                <label className="switch">
                                  <input
                                    type="checkbox"
                                    checked={
                                      formik.values.roofDetails?.[index]?.pyranoMeter
                                        ?.forwardPyranometerData || false
                                    }
                                    onChange={(value) =>
                                      formik.setFieldValue(
                                        `roofDetails.${index}.pyranoMeter.forwardPyranometerData`,
                                        !formik.values.roofDetails?.[index]?.pyranoMeter
                                          ?.forwardPyranometerData
                                      )
                                    }
                                  />
                                  <span className="slider"></span>
                                </label>
                              </div>
                            </div>
                          </div>
                        </Grid>

                        {formik.values.roofDetails?.[index]?.pyranoMeter?.forwardPyranometerData && (
                          <>
                        <Grid item xs={12} md={4} mt={1}>
                          <div className=" textfield">
                            <CustomTextField
                              Adornmenttype={true}
                              label="Tilt Angle"
                              Adornmentlabel="degree"
                              type="number"
                              Adornmentposition="end"
                              id="filled-adornment-weight"
                              value={formik.values.roofDetails?.[index]?.tiltAngle}
                              onChange={(value) =>
                                formik.setFieldValue(
                                  `roofDetails.${index}.tiltAngle`,
                                  Number(value.target.value)
                                )
                              }
                              error={formik.errors?.roofDetails?.[index]?.tiltAngle}
                              helperText={
                                get(
                                  formik,
                                  `touched.roofDetails.${index}.tiltAngle`
                                ) &&
                                get(formik, `errors.roofDetails.${index}.tiltAngle`)
                              }
                            />
                          </div>
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <div
                            className={classes.estimationButton}
                            style={{ width: '100%' }}
                            onClick={() => setEstimatedGTIOpen(true)}
                          >
                            Multiplying factor
                          </div>
                        </Grid>

                        <Dialog
                          className="businessview_dialog"
                          open={estimatedGTIOpen}
                          onClose={handleestimatedGTIClose}
                          maxWidth="md"
                          fullWidth
                        >
                          <DialogTitle>
                            <div className="align_btw brdr">
                              <div className="h_1">Multiplying factor </div>

                              <DialogActions>
                                <ClearIcon
                                  onClick={handleestimatedGTIClose}
                                  className="pointer"
                                />
                              </DialogActions>
                            </div>
                          </DialogTitle>
                          <DialogContent>
                            <EstimatedGeneration
                              formik={formik}
                              Type={`roofDetails.${index}.pyranoMeter.multiplyingFactor`}
                              OgType={`roofDetails.${index}.pyranoMeter.multiplyingFactorOg`}
                              Edit={Edit}
                              FormikFields={
                                formik.values.roofDetails?.[index]?.pyranoMeter
                                  .multiplyingFactorOg
                              }
                            />
                            <DialogActions></DialogActions>
                          </DialogContent>
                        </Dialog>
                        <Grid item xs={12} md={4}>
                          <div className=" textfield ">
                            <CustomTextField
                              label="Make & Model"
                              id="filled-size-small"
                              value={
                                formik.values.roofDetails?.[index]
                                  ?.pyranometerMakeAndModel
                              }
                              onChange={(value) =>
                                formik.setFieldValue(
                                  `roofDetails.${index}.pyranometerMakeAndModel`,
                                  value.target.value
                                )
                              }
                              error={
                                formik.errors?.roofDetails?.[index]
                                  ?.pyranometerMakeAndModel
                              }
                              helperText={
                                get(
                                  formik,
                                  `touched.roofDetails.${index}.pyranometerMakeAndModel`
                                ) &&
                                get(
                                  formik,
                                  `errors.roofDetails.${index}.pyranometerMakeAndModel`
                                )
                              }
                            />
                          </div>
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <div className=" textfield ">
                            <CustomTextField
                              label="Pyranometer Serial Number"
                              id="filled-size-small"
                              value={
                                formik.values.roofDetails?.[index]?.pyranoMeter
                                  ?.pyranoMeterSerialNumber
                              }
                              onChange={(value) =>
                                formik.setFieldValue(
                                  `roofDetails.${index}.pyranoMeter.pyranoMeterSerialNumber`,
                                  value.target.value
                                )
                              }
                              error={
                                formik.errors?.roofDetails?.[index]?.pyranoMeter
                                  ?.pyranoMeterSerialNumber
                              }
                              helperText={
                                get(
                                  formik,
                                  `touched.roofDetails.${index}.pyranoMeter.pyranoMeterSerialNumber`
                                ) &&
                                get(
                                  formik,
                                  `errors.roofDetails.${index}.pyranoMeter.pyranoMeterSerialNumber`
                                )
                              }
                            />
                          </div>
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <div className=" textfield ">
                            <AutocompleteDropdown
                              label="Device Make"
                              value={
                                formik.values.roofDetails?.[index]?.pyranoMeter
                                  ?.deviceMake
                              }
                              onChange={(value) =>
                                formik.setFieldValue(
                                  `roofDetails.${index}.pyranoMeter.deviceMake`,
                                  value.target.value
                                )
                              }
                              options={sensorOptions.makes}
                              error={
                                formik.errors?.roofDetails?.[index]?.pyranoMeter
                                  ?.deviceMake
                              }
                              helperText={
                                (get(
                                  formik,
                                  `touched.roofDetails.${index}.pyranoMeter.deviceMake`
                                ) ||
                                  formik.submitCount > 0) &&
                                get(
                                  formik,
                                  `errors.roofDetails.${index}.pyranoMeter.deviceMake`
                                )
                              }
                              placeholder="Select or type custom value"
                            />
                          </div>
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <div className=" textfield ">
                            <AutocompleteDropdown
                              label="Device Model"
                              value={
                                formik.values.roofDetails?.[index]?.pyranoMeter
                                  ?.deviceModel
                              }
                              onChange={(value) =>
                                formik.setFieldValue(
                                  `roofDetails.${index}.pyranoMeter.deviceModel`,
                                  value.target.value
                                )
                              }
                              options={sensorOptions.models}
                              error={
                                formik.errors?.roofDetails?.[index]?.pyranoMeter
                                  ?.deviceModel
                              }
                              helperText={
                                (get(
                                  formik,
                                  `touched.roofDetails.${index}.pyranoMeter.deviceModel`
                                ) ||
                                  formik.submitCount > 0) &&
                                get(
                                  formik,
                                  `errors.roofDetails.${index}.pyranoMeter.deviceModel`
                                )
                              }
                              placeholder="Select or type custom value"
                            />
                          </div>
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <div className=" textfield ">
                            <CustomTextField
                              label="Slave ID"
                              id="filled-size-small"
                              disabled={Edit && !formik.values.roofDetails?.[index]?.pyranoMeter?.forwardPyranometerData}
                              type="number"
                              value={
                                formik.values.roofDetails?.[index]?.pyranoMeter
                                  ?.slaveId
                              }
                              onChange={(value) => {
                                formik.setFieldValue(
                                  `roofDetails.${index}.pyranoMeter.slaveId`,
                                  Number(value.target.value)
                                )
                                formik.setFieldTouched(
                                  `roofDetails.${index}.pyranoMeter.slaveId`,
                                  true,
                                  true
                                )
                              }}
                              error={
                                formik.errors?.roofDetails?.[index]?.pyranoMeter
                                  ?.slaveId
                              }
                              helperText={
                                (get(
                                  formik,
                                  `touched.roofDetails.${index}.pyranoMeter.slaveId`
                                ) ||
                                  formik.submitCount > 0) &&
                                get(
                                  formik,
                                  `errors.roofDetails.${index}.pyranoMeter.slaveId`
                                )
                              }
                            />
                          </div>
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <div className=" textfield ">
                            <CustomTextField
                              label="Channel"
                              id="filled-size-small"
                              type="number"
                              value={
                                formik.values.roofDetails?.[index]?.pyranoMeter
                                  ?.channel || 1
                              }
                              onChange={(value) =>
                                formik.setFieldValue(
                                  `roofDetails.${index}.pyranoMeter.channel`,
                                  Number(value.target.value)
                                )
                              }
                            />
                          </div>
                        </Grid>
                          </>
                        )}
                      </Grid>
                    )
                  })()}
                </Box>
              </TabPanel>
            </TabContext>
          ))}
        </Box>
      </div>
    </div>
  )
}

EquipmentDetails.propTypes = {}

export default EquipmentDetails